import React from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { createBrowserRouter, redirect } from 'react-router-dom';
import App from './App';
import { authLoader, nonAuthLoader } from './auth';
import CatchBoundary from './error/CatchBoundary';
import NotFound from './error/NotFound';
import Contact from './pages/Contact';
import FAQ from './pages/faq/FAQ';
import HelpCenter from './pages/HelpCenter';
import Home from './pages/Home';
import Exercise from './pages/learn/Exercise';
import Learn from './pages/learn/Learn';
import QRResults from './pages/learn/qr-results/QRResults';
import ScanQR from './pages/learn/ScanQR';
import LearnSimulation from './pages/learn/Simulation';
import Login from './pages/Login';
import OldPortalMessage from './pages/OldPortalMessage';
import Privacy from './pages/Privacy';
import Profile from './pages/profile/Profile';
import Register from './pages/Register';
import RegistrationConfirmation from './pages/RegistrationConfirmation';
import ServiceLevelAgreement from './pages/ServiceLevelAgreement';
import Classroom from './pages/teach/Classroom';
import TeachExercise from './pages/teach/Exercise';
import ExerciseStudent from './pages/teach/ExerciseStudent';
import TeachSimulation from './pages/teach/Simulation';
import StudentSimulation from './pages/teach/StudentSimulation';
import Teach from './pages/teach/Teach';
import TermsOfUse from './pages/TermsOfUse';
import { apiPaths, handleResponse, queryClient } from './queryClient';
import { Profile as IProfile, UserRole } from './types';
import { Pages } from './types/pages';

export const createRouter = () =>
  createBrowserRouter([
    {
      path: '/v2',
      ...(import.meta?.env?.DEV
        ? { element: <OldPortalMessage /> }
        : {
            loader: () => {
              window.location.href = '/v2/';
              return null;
            },
          }),
    },
    {
      path: '/',
      id: 'root',
      element: <App />,
      errorElement: <CatchBoundary />,
      children: [
        {
          path: '/',
          loader: async request => {
            const authResponse = authLoader(request);
            if (authResponse) {
              return authResponse;
            }

            const profile = await queryClient.fetchQuery<IProfile>({
              queryKey: [{ path: apiPaths.me, cache: 'no-cache' }],
            });
            const roles = profile?.companies[0]?.roles;

            return !import.meta?.env?.DEV
              ? redirect(
                  roles?.includes(UserRole.Instructor)
                    ? Pages.Teach
                    : roles?.includes(UserRole.Student)
                    ? Pages.Learn
                    : '/home',
                )
              : redirect(Pages.Learn); // Only for local development
          },
        },
        { path: '/profile', element: <Profile />, errorElement: <CatchBoundary />, loader: authLoader },
        {
          path: '/learn',
          children: [
            { index: true, element: <Learn />, errorElement: <CatchBoundary />, loader: authLoader },
            {
              path: 'exercises/:exerciseId',
              children: [
                {
                  index: true,
                  element: <Exercise />,
                  errorElement: <CatchBoundary />,
                  loader: authLoader,
                },
                {
                  path: 'simulations/:simulationId',
                  element: <LearnSimulation />,
                  errorElement: <CatchBoundary />,
                  loader: authLoader,
                },
              ],
            },
            {
              path: 'scan-qr',
              children: [
                { index: true, element: <ScanQR />, errorElement: <CatchBoundary />, loader: authLoader },
                { path: ':simulatorId', element: <QRResults />, errorElement: <CatchBoundary />, loader: authLoader },
              ],
            },
          ],
        },
        {
          path: '/teach',
          children: [
            { index: true, element: <Teach />, errorElement: <CatchBoundary />, loader: authLoader },
            {
              path: 'exercises/:exerciseId',
              children: [
                { index: true, element: <TeachExercise />, errorElement: <CatchBoundary />, loader: authLoader },
                {
                  path: 'simulations/:simulationId',
                  element: <TeachSimulation />,
                  errorElement: <CatchBoundary />,
                  loader: authLoader,
                },
                {
                  path: 'students/:userId',
                  children: [
                    { index: true, element: <ExerciseStudent />, errorElement: <CatchBoundary />, loader: authLoader },
                    {
                      path: 'simulations/:simulationId',
                      element: <StudentSimulation />,
                      errorElement: <CatchBoundary />,
                      loader: authLoader,
                    },
                  ],
                },
                {
                  path: Pages.Classroom,
                  element: <Classroom />,
                  errorElement: <CatchBoundary />,
                  loader: authLoader,
                },
              ],
            },
          ],
        },
        { path: '/home', element: <Home />, loader: authLoader },
        { path: '/login', element: <Login />, loader: nonAuthLoader },
        {
          path: '/register',

          children: [
            {
              index: true,
              element: (
                <GoogleReCaptchaProvider
                  useRecaptchaNet={false}
                  reCaptchaKey={import.meta.env.VITE_RECAPTCHA_SITE_KEY as string}
                  scriptProps={{ async: true, defer: true, appendTo: 'body' }}
                >
                  <Register />
                </GoogleReCaptchaProvider>
              ),
              errorElement: <CatchBoundary />,
              loader: nonAuthLoader,
              action: async ({ request }) => {
                const formData = await request.formData();
                const updates = Object.fromEntries(formData);
                try {
                  await fetch('/api/sign-up', {
                    body: JSON.stringify(updates),
                    headers: { 'Content-Type': 'application/json' },
                    method: 'POST',
                  }).then(handleResponse);

                  return redirect(`confirmation?email=${updates.email}`);
                } catch (response: unknown) {
                  const res = response as Response;
                  const data = await (res.headers.get('Content-Type')?.includes('application/json') ||
                  res.headers.get('Content-Type')?.includes('application/problem+json')
                    ? res.json()
                    : res.text());
                  return data;
                }
              },
            },
            { path: 'confirmation', element: <RegistrationConfirmation />, errorElement: <CatchBoundary /> },
          ],
        },
        {
          path: '/privacy',
          element: <Privacy />,
          loader: () => queryClient.fetchQuery({ queryKey: [{ isPublic: true, path: 'trust-center/privacy' }] }),
        },
        {
          path: '/service-level-agreement',
          element: <ServiceLevelAgreement />,
          loader: () =>
            queryClient.fetchQuery({ queryKey: [{ isPublic: true, path: 'trust-center/service-level-agreement' }] }),
        },
        {
          path: '/terms-of-use',
          element: <TermsOfUse />,
          loader: () => queryClient.fetchQuery({ queryKey: [{ isPublic: true, path: 'trust-center/terms-of-use' }] }),
        },
        {
          path: '/help-center',
          children: [
            { index: true, element: <HelpCenter />, errorElement: <CatchBoundary />, loader: authLoader },
            {
              path: 'contact-us',
              children: [
                {
                  index: true,
                  element: <Contact />,
                  errorElement: <CatchBoundary />,
                  loader: authLoader,
                },
              ],
            },
            {
              path: 'faq',
              children: [
                {
                  index: true,
                  element: <FAQ />,
                  errorElement: <CatchBoundary />,
                  loader: authLoader,
                },
              ],
            },
          ],
        },
        { path: '*', element: <NotFound /> },
      ],
    },
  ]);
